import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowRight, ChevronLeft, ShieldCheck } from "lucide-react";
import apiService from "../services/api";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    
    try {
      const response = await apiService.post('/auth/forgot-password', { email });
      if (response.success) {
        setIsSent(true);
        setIsSubmitting(false);
      }
    } catch (err) {
      setError(err.message || t('auth.tech_error'));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-1/4 left-1/4 w-full max-w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-full max-w-[400px] h-[400px] bg-violet-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <button 
          onClick={() => navigate("/login")}
          className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors mb-8 group"
        >
          <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-black uppercase tracking-widest">{t('auth.back_to_login')}</span>
        </button>

        <div className="bg-white/[0.02] border border-white/5 p-10 rounded-[32px] backdrop-blur-xl shadow-2xl">
          {!isSent ? (
            <>
              <div className="w-16 h-16 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl flex items-center justify-center text-indigo-400 mb-8">
                <Mail size={32} />
              </div>
              
              <h1 className="text-3xl font-black font-outfit tracking-tight text-white mb-3">
                {t('auth.forgot_password')}
              </h1>
              <p className="text-slate-400 font-medium mb-8 leading-relaxed">
                {t('auth.forgot_password_desc')}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                    {t('auth.email')}
                  </label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                      <Mail size={18} />
                    </div>
                    <input
                      type="email"
                      required
                      placeholder="nom@exemple.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-slate-950/50 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white outline-none focus:border-indigo-500/50 focus:bg-slate-950 transition-all font-medium"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs font-bold">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 rounded-2xl font-black text-xs uppercase tracking-[0.2em] text-white transition-all shadow-xl shadow-indigo-500/20 flex items-center justify-center gap-3 active:scale-95"
                >
                  {isSubmitting ? t('auth.sending') : t('auth.send_link')}
                  {!isSubmitting && <ArrowRight size={18} />}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-400 mb-8 mx-auto">
                <ShieldCheck size={32} />
              </div>
              
              <h1 className="text-3xl font-black font-outfit tracking-tight text-white mb-3">
                {t('auth.check_emails')}
              </h1>
              <p className="text-slate-400 font-medium mb-10 leading-relaxed">
                {t('auth.code_sent_to')} <span className="text-white">{email}</span>. 
                {t('auth.check_spam')}
              </p>

              <button
                onClick={() => navigate("/reset-password")}
                className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl font-black text-xs uppercase tracking-[0.2em] text-white transition-all"
              >
                {t('auth.go_enter_code')}
              </button>
              
              <p className="mt-8 text-slate-500 text-xs font-medium">
                {t('auth.didnt_receive')} {" "}
                <button 
                  onClick={() => setIsSent(false)} 
                  className="text-indigo-400 hover:text-indigo-300 font-bold underline underline-offset-4"
                >
                  {t('auth.try_again')}
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
